'use client';
import React, { useState, useCallback, useEffect } from 'react';
import { StatsCard } from '@/components/molecules/StatsCard';
import { IEstatistica } from '@/services/types/PaginaComponent';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/atoms/carousel';

interface StatsSectionProps {
  title?: string;
  subtitle?: string;
  backgroundImage?: string;
  indicadores?: IEstatistica['indicadores'];
  className?: string;
}

export const StatsSection: React.FC<StatsSectionProps> = ({
  title = 'Veja o nosso progresso e compromisso com a excelência',
  subtitle = 'Indicadores',
  backgroundImage = '/bg-indicadores.png',
  indicadores = [],
  className = ''
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [carouselCurrentIndex, setCarouselCurrentIndex] = useState(0);
  const [carouselCount, setCarouselCount] = useState(0);

  // Carousel API handlers
  useEffect(() => {
    if (!carouselApi) return;

    setCarouselCount(carouselApi.scrollSnapList().length);
    setCarouselCurrentIndex(carouselApi.selectedScrollSnap());

    carouselApi.on('select', () => {
      setCarouselCurrentIndex(carouselApi.selectedScrollSnap());
    });
  }, [carouselApi]);

  const scrollToIndex = useCallback((index: number) => {
    carouselApi?.scrollTo(index);
  }, [carouselApi]);

  // Cores padrão para os cards (ciclo de cores)
  const defaultColors = [
    { circleColor: 'bg-[#598648]', accentColor: 'bg-yellow-300' },
    { circleColor: 'bg-[#82B662]', accentColor: 'bg-yellow-300' },
    { circleColor: 'bg-[#F05A49]', accentColor: 'bg-yellow-300' },
    { circleColor: 'bg-[#F05A49]', accentColor: 'bg-yellow-300' },
    { circleColor: 'bg-[#377396]', accentColor: 'bg-yellow-300' },
  ];


  // Mapear indicadores da API
  const stats = indicadores.map((indicador, index) => {
    const iconUrl = indicador.image && indicador.image.length > 0 && indicador.image[0].url
      ? (indicador.image[0].url.startsWith('http') 
          ? indicador.image[0].url 
          : `${process.env.NEXT_PUBLIC_API_URL || ''}${indicador.image[0].url}`)
      : undefined;
    
    return {
      number: indicador.total || '0',
      title: indicador.indicadores ? indicador.indicadores.split('\n') : ['Indicador'],
      iconUrl: iconUrl,
      icon: undefined,
      circleColor: defaultColors[index % defaultColors.length].circleColor,
      accentColor: defaultColors[index % defaultColors.length].accentColor,
    };
  });

  return (
    <section 
      className={`relative w-full py-28 overflow-hidden ${className}`}
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'fill',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        width: '100%',
      }}
    >
      
      {/* Conteúdo */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16">
        {/* Cabeçalho */}
        <div className="text-left mb-12 ">
          
          <h2 className="text-white text-3xl md:text-4xl font-normal font-['Rubik'] leading-tight max-w-xl pt-12 mb-4 xl:pt-18">
            {title}
          </h2>
          <div className="inline-block bg-white/20 rounded-[30px] px-6 py-2 mb-4">
            <span className="text-white text-base font-normal font-['Poppins']">
              {subtitle}
            </span>
          </div>
        </div>

        {/* Desktop: Grid (apenas xl+) */}
        {stats.length <= 5 ? (
          <div className="hidden xl:grid xl:grid-cols-5 gap-6 lg:gap-8 justify-items-center">
            {stats.map((stat, index) => (
              <StatsCard
                key={index}
                number={stat.number}
                title={stat.title}
                icon={stat.icon}
                iconUrl={(stat as any).iconUrl}
                circleColor={stat.circleColor}
                accentColor={stat.accentColor}
              />
            ))}
          </div>
        ) : (
          <div className="hidden xl:block">
            <div className="grid xl:grid-cols-5 gap-6 lg:gap-8 justify-items-center">
              {stats.slice(currentIndex * 5, (currentIndex + 1) * 5).map((stat, index) => (
                <div key={currentIndex * 5 + index} className="w-full flex justify-center">
                  <StatsCard
                    number={stat.number}
                    title={stat.title}
                    icon={stat.icon}
                    iconUrl={(stat as any).iconUrl}
                    circleColor={stat.circleColor}
                    accentColor={stat.accentColor}
                  />
                </div>
              ))}
            </div>
            {/* Pagination Dots */}
            {stats.length > 5 && (
              <div className="flex justify-center gap-3 mt-8">
                {Array.from({ length: Math.ceil(stats.length / 5) }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`cursor-pointer rounded-full transition-all ${
                      index === currentIndex
                        ? 'w-12 h-3 bg-white'
                        : 'w-3 h-3 bg-white/50 hover:bg-white/70'
                    }`}
                    aria-label={`Ir para página ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Mobile e Tablet: Carrossel */}
        <div className="xl:hidden">
          <Carousel className="w-full" setApi={setCarouselApi}>
            <CarouselContent className="-ml-2 md:-ml-4">
              {stats.map((stat, index) => (
                <CarouselItem key={index} className="pl-2 md:pl-4 basis-[75%] sm:basis-[60%] md:basis-[45%] lg:basis-[35%]">
                  <div className="flex justify-center">
                    <StatsCard
                      number={stat.number}
                      title={stat.title}
                      icon={stat.icon}
                      iconUrl={(stat as any).iconUrl}
                      circleColor={stat.circleColor}
                      accentColor={stat.accentColor}
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          {/* Pagination Dots - Mobile e Tablet */}
          {carouselCount > 1 && (
            <div className="flex justify-center gap-2 sm:gap-3 mt-6">
              {Array.from({ length: carouselCount }).map((_, index) => (
                <button
                  key={`stats-dot-${index}`}
                  onClick={() => scrollToIndex(index)}
                  className={`cursor-pointer rounded-full transition-all ${
                    index === carouselCurrentIndex
                      ? 'w-10 sm:w-12 h-3 bg-white'
                      : 'w-3 h-3 bg-white/50 hover:bg-white/70'
                  }`}
                  aria-label={`Ir para slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
